import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import { Home, Building2, HardHat, CheckCircle2 } from 'lucide-react';

function ServiceCard({ icon: Icon, title, description, features, sizes, pricing, popular }) {
  return (
    <Card className="border-border hover:shadow-elegant transition-smooth flex flex-col group">
      <CardContent className="pt-6 flex flex-col h-full">
        {popular && <Badge className="mb-4 w-fit bg-accent text-accent-foreground">Popular</Badge>}
        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-gradient-primary transition-smooth">
          <Icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-smooth" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-3">{title}</h3>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        <div className="mb-4 flex-grow">
          <div className="text-xs font-semibold text-foreground mb-2">Key Features:</div>
          <ul className="space-y-2">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-2">Available Sizes:</div>
          <div className="flex flex-wrap gap-2">
            {sizes.map((size, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">{size}</Badge>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
          <span className="text-lg font-bold text-primary">{pricing}</span>
          <Button asChild size="sm" className="bg-gradient-primary hover:shadow-glow transition-smooth">
            <Link to="/contact">Get Quote</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ServicesPage() {
  const services = [
    {
      icon: Home,
      title: 'Residential Dumpster Rental',
      description: 'Perfect for home renovations, spring cleaning, estate cleanouts, and moving projects.',
      features: [
        'Same-day or next-day delivery',
        'Flexible rental periods',
        'Multiple size options (10-20 yards)',
        'Driveway-safe placement',
      ],
      sizes: ['10 Yard', '15 Yard', '20 Yard'],
      pricing: 'Starting at $299',
      popular: true,
    },
    {
      icon: Building2,
      title: 'Commercial Waste Services',
      description: 'Reliable waste management solutions for businesses, retail stores, and office buildings.',
      features: [
        'Scheduled pickups',
        'Flexible contracts',
        'Multiple dumpsters available',
        'Recycling programs',
      ],
      sizes: ['20 Yard', '30 Yard', '40 Yard'],
      pricing: 'Starting at $449',
      popular: true,
    },
    {
      icon: HardHat,
      title: 'Construction Debris Removal',
      description: 'Heavy-duty dumpsters for construction sites, demolition projects, and contractors.',
      features: [
        'Heavy materials accepted',
        'High weight limits',
        'Site delivery coordination',
        'Multiple unit discounts',
      ],
      sizes: ['30 Yard', '40 Yard'],
      pricing: 'Starting at $549',
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative pt-32 pb-16 md:pb-24 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-primary text-primary-foreground border-0">Comprehensive Waste Solutions</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">Our Services</h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Professional dumpster rental and waste management services for residential, commercial, and construction projects.
            </p>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <Card className="border-0 bg-gradient-primary shadow-elegant">
            <CardContent className="py-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-6">Not Sure Which Size You Need?</h2>
              <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
                Our expert team is here to help you choose the perfect dumpster size and service for your project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-elegant text-base">
                  <Link to="/contact">Contact Us</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-primary-foreground/10 backdrop-blur-sm border-primary-foreground text-primary-foreground hover:bg-primary-foreground/20 text-base">
                  <a href="tel:1-800-DUMPSTER">Call: 1-800-DUMPSTER</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
}
